import React, { useState } from 'react';
import { BarChart3, TrendingUp, Download, Filter } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { formatNumber, formatCurrency, formatPercent, calculateChange } from '../utils/helpers';

const Analytics = () => {
    const { analytics } = useApp();
    const [dateRange, setDateRange] = useState('30d');
    const [selectedPlatform, setSelectedPlatform] = useState('all');

    const kpis = [
        {
            label: 'Total Impressions',
            value: formatNumber(analytics.overview.impressions.current),
            change: calculateChange(analytics.overview.impressions.current, analytics.overview.impressions.previous),
        },
        {
            label: 'Total Clicks',
            value: formatNumber(analytics.overview.clicks.current),
            change: calculateChange(analytics.overview.clicks.current, analytics.overview.clicks.previous),
        },
        {
            label: 'Conversions',
            value: formatNumber(analytics.overview.conversions.current),
            change: calculateChange(analytics.overview.conversions.current, analytics.overview.conversions.previous),
        },
        {
            label: 'Ad Spend',
            value: formatCurrency(analytics.overview.spend.current),
            change: calculateChange(analytics.overview.spend.current, analytics.overview.spend.previous),
        },
        {
            label: 'Revenue',
            value: formatCurrency(analytics.overview.revenue.current),
            change: calculateChange(analytics.overview.revenue.current, analytics.overview.revenue.previous),
        },
        {
            label: 'ROAS',
            value: (analytics.overview.revenue.current / analytics.overview.spend.current).toFixed(2) + 'x',
            change: calculateChange(
                analytics.overview.revenue.current / analytics.overview.spend.current,
                analytics.overview.revenue.previous / analytics.overview.spend.previous
            ),
        },
    ];

    return (
        <div className="animate-fadeIn">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-8)' }}>
                <div>
                    <h1>Performance Analytics</h1>
                    <p className="text-secondary">Track campaign performance across all platforms.</p>
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                    <select className="select-field" value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
                        <option value="7d">Last 7 days</option>
                        <option value="30d">Last 30 days</option>
                        <option value="90d">Last 90 days</option>
                    </select>
                    <button className="btn btn-secondary">
                        <Download size={16} />
                        Export
                    </button>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-3 gap-6" style={{ marginBottom: 'var(--space-8)' }}>
                {kpis.map((kpi, index) => (
                    <div key={index} className="stat-card">
                        <div className="stat-label">{kpi.label}</div>
                        <div className="stat-value">{kpi.value}</div>
                        <div className={`stat-change ${kpi.change >= 0 ? 'positive' : 'negative'}`}>
                            <TrendingUp size={16} />
                            {formatPercent(Math.abs(kpi.change))} vs previous period
                        </div>
                    </div>
                ))}
            </div>

            {/* Platform Performance */}
            <div className="card" style={{ marginBottom: 'var(--space-8)' }}>
                <h2 style={{ marginBottom: 'var(--space-6)' }}>Platform Performance</h2>
                <div className="table-wrapper">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Platform</th>
                                <th>Impressions</th>
                                <th>Clicks</th>
                                <th>CTR</th>
                                <th>Conversions</th>
                                <th>Spend</th>
                                <th>ROAS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {analytics.platformPerformance.map((platform, index) => (
                                <tr key={index}>
                                    <td style={{ fontWeight: 600 }}>{platform.platform}</td>
                                    <td>{formatNumber(platform.impressions)}</td>
                                    <td>{formatNumber(platform.clicks)}</td>
                                    <td>{formatPercent(platform.ctr)}</td>
                                    <td>{formatNumber(platform.conversions)}</td>
                                    <td>{formatCurrency(platform.spend)}</td>
                                    <td>
                                        <span className={platform.roas >= 5 ? 'stat-change positive' : ''}>
                                            {platform.roas.toFixed(1)}x
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Performance Trend Chart */}
            <div className="card" style={{ marginBottom: 'var(--space-8)' }}>
                <h2 style={{ marginBottom: 'var(--space-6)' }}>Performance Trend</h2>
                <div style={{ height: '300px', display: 'flex', alignItems: 'flex-end', gap: 'var(--space-2)' }}>
                    {analytics.performanceTrend.map((point, index) => {
                        const maxConversions = Math.max(...analytics.performanceTrend.map(p => p.conversions));
                        const height = (point.conversions / maxConversions) * 100;

                        return (
                            <div
                                key={index}
                                style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                }}
                            >
                                <div
                                    style={{
                                        width: '100%',
                                        height: `${height}%`,
                                        background: 'var(--gradient-primary)',
                                        borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
                                        transition: 'all var(--transition-base)',
                                        cursor: 'pointer',
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scaleY(1.05)'}
                                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scaleY(1)'}
                                    title={`${point.conversions} conversions`}
                                />
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                    {new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Top Performing Creatives */}
            <div className="card">
                <h2 style={{ marginBottom: 'var(--space-6)' }}>Top Performing Creatives</h2>
                <div className="table-wrapper">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Creative</th>
                                <th>Impressions</th>
                                <th>CTR</th>
                                <th>Conversions</th>
                                <th>Revenue</th>
                                <th>ROAS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {analytics.topCreatives.map((creative) => (
                                <tr key={creative.id}>
                                    <td style={{ fontWeight: 600 }}>{creative.title}</td>
                                    <td>{formatNumber(creative.impressions)}</td>
                                    <td>{formatPercent(creative.ctr)}</td>
                                    <td>{creative.conversions}</td>
                                    <td>{formatCurrency(creative.revenue)}</td>
                                    <td>
                                        <span className="stat-change positive">
                                            {creative.roas.toFixed(1)}x
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Analytics;
